// apps/admin/admin-main/src/pages/UberDeliveries/index.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, Chip, IconButton, Tooltip, Tabs, Tab,
  CircularProgress, Grid, Alert, TextField, MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import config from '../../config';
import { httpClient } from '../../components/fetchApiData';
import DeliveryCard, { UberDelivery } from './DeliveryCard';
import DeliveryDetail from './DeliveryDetail';
import CreateQuoteDialog from './CreateQuoteDialog';
import { TERMINAL_STATUSES, AUTO_REFRESH_INTERVAL_MS, STATUS_LABELS } from './constants';

export default function UberDeliveries() {
  const [deliveries, setDeliveries] = useState<UberDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0); // 0 = Active, 1 = History
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDeliveries = useCallback(async (filter?: string) => {
    try {
      const params = new URLSearchParams();
      if (filter) params.append('filter', filter);
      const { json } = await httpClient(
        `${config.API_URL}/admin/uber-deliveries?${params}`,
      );
      setDeliveries(json.data || []);
      setError(null);
    } catch (e: any) {
      setError(e.body?.message || e.message);
      // Keep stale data on error
    }
    setLoading(false);
  }, []);

  // Fetch on mount and tab change
  useEffect(() => {
    setLoading(true);
    fetchDeliveries(tab === 0 ? 'active' : undefined);
  }, [tab, fetchDeliveries]);

  // Auto-refresh for Active tab
  useEffect(() => {
    if (tab !== 0) return;
    intervalRef.current = setInterval(() => {
      fetchDeliveries('active');
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tab, fetchDeliveries]);

  const activeDeliveries = deliveries.filter((d) => !TERMINAL_STATUSES.includes(d.status));

  const historyDeliveries = deliveries.filter((d) => {
    if (historyStatusFilter && d.status !== historyStatusFilter) return false;
    if (historySearch) {
      const q = historySearch.toLowerCase();
      const matchesId = d.id?.toLowerCase().includes(q);
      const matchesOrder = d.deeliver_order?.orderNumber?.toString().toLowerCase().includes(q);
      if (!matchesId && !matchesOrder) return false;
    }
    return true;
  });

  const displayDeliveries = tab === 0 ? activeDeliveries : historyDeliveries;

  const handleCardClick = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={700}>Uber Deliveries</Typography>
          <Chip label={`${activeDeliveries.length} active`} size="small" color="primary" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Create Quote">
            <IconButton onClick={() => setQuoteOpen(true)} color="primary">
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchDeliveries(tab === 0 ? 'active' : undefined)} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Active" />
        <Tab label="History" />
      </Tabs>

      {tab === 1 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            label="Status"
            value={historyStatusFilter}
            onChange={(e) => setHistoryStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search by ID or order #"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            size="small"
            sx={{ minWidth: 250 }}
          />
        </Stack>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }}>Failed to refresh: {error}</Alert>}

      {loading && deliveries.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : displayDeliveries.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <Typography color="text.secondary">
            {tab === 0 ? 'No active deliveries' : 'No deliveries found'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {displayDeliveries.map((delivery) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={delivery.id}>
              <DeliveryCard
                delivery={delivery}
                expanded={expandedId === delivery.id}
                onClick={() => handleCardClick(delivery.id)}
              />
              {expandedId === delivery.id && (
                <DeliveryDetail
                  delivery={delivery}
                  onRefresh={() => fetchDeliveries(tab === 0 ? 'active' : undefined)}
                />
              )}
            </Grid>
          ))}
        </Grid>
      )}

      <CreateQuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </Box>
  );
}
